// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ChargeListItem } from '@/billing/api/types';
import { ChargePreviewCard } from '@/billing/components/charge-preview-card';
import { useRevertChargeCancellation } from '@/billing/hooks/use-revert-charge-cancellation';
import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

interface Props {
  isOpen: boolean;
  charge: ChargeListItem | null;
  onClose: () => void;
  onError?: (
    message: string,
    apiStatusCode: number,
    httpStatusCode: number
  ) => void;
}

/**
 * Confirms reverting a previously-cancelled charge back to an active state.
 * The destination state is determined server-side from the schedule date.
 * No reason is collected — the audit captures the user and timestamp.
 */
export function RevertChargeCancellationDialog(props: Props) {
  const close = () => {
    props.onClose();
  };

  const revertMutation = useRevertChargeCancellation({
    onSuccess: () => {
      close();
    },
    onError: (message, apiStatusCode, httpStatusCode) => {
      close();
      props.onError?.(message, apiStatusCode, httpStatusCode);
    },
  });

  const handleConfirm = () => {
    if (!props.charge) {
      return;
    }
    revertMutation.mutate(props.charge.key);
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={props.isOpen} onClose={close}>
      <DialogTitle
        sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 2 }}
      >
        Revert cancellation
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        <Alert severity="info" sx={{ marginY: 2, alignItems: 'center' }}>
          <AlertTitle variant="h6">
            Revert cancellation for <strong>{props.charge?.label ?? ''}</strong>
            ?
          </AlertTitle>
          <Typography variant="body1">
            The charge will return to &quot;planned&quot; status and become
            eligible for billing again
          </Typography>
        </Alert>

        {props.charge && <ChargePreviewCard charge={props.charge} />}
      </DialogContent>

      <DialogActions
        sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}
      >
        <Button
          onClick={close}
          variant="outlined"
          disabled={revertMutation.isPending}
        >
          No, keep cancelled
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={revertMutation.isPending || props.charge === null}
          sx={{ ml: 1.5 }}
        >
          Yes, revert cancellation
        </Button>
      </DialogActions>
    </Dialog>
  );
}
